<script setup lang="ts">
import { computed, useAttrs } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";
import { popperThemeClasses } from "../Popper/lazyPopper";

defineOptions({
  inheritAttrs: false,
});

const {
  allowChange = false,
  type = "button",
  popperClass = undefined,
  showGroup = undefined,
  customContent = undefined,
  disabledReason = undefined,
  placement = "top",
} = defineProps<{
  allowChange?: boolean
  type?: "dropdown_item" | "edit_field" | "button" | "tab" | "list" | "item" | "delete" | "action" | "comment"
  popperClass?: string
  showGroup?: string
  customContent?: string
  /**
   * Why the control is disabled although the user may change it, e.g. "a hint with sub hints cannot be nested".
   * Shown instead of the permission message, never next to it: without permission the permission message wins.
   */
  disabledReason?: string
  placement?: "auto" | "auto-start" | "auto-end" | "top" | "top-start" | "top-end" | "bottom" | "bottom-start" | "bottom-end" | "right" | "right-start" | "right-end" | "left" | "left-start" | "left-end"
}>();

const THEME = "verbleif-tooltip-ac";

const attrs = useAttrs();
const { t } = useI18n();

const permissionMessage = computed(() => customContent ?? t(`permissions.ac.${type}`));
const reason = computed(() => (disabledReason && disabledReason.trim() !== "" ? disabledReason : null));

// Same root element a disabled VTooltip renders, so allowed and denied states have identical layout.
const allowedClasses = computed(() => [popperThemeClasses(THEME), attrs.class]);
</script>

<template>
  <!-- The tooltip only explains a denied or disabled control; otherwise the wrapper is plain markup. -->
  <div v-if="allowChange && !reason" class="v-popper" :class="allowedClasses">
    <slot />
  </div>
  <VTooltip
    v-else-if="allowChange"
    :placement="placement"
    class="cursor-not-allowed"
    :class="attrs.class"
    :triggers="['hover', 'focus', 'touch']"
    :show-group="showGroup"
    :popper-class="popperClass"
    data-testid="disabled-reason-tooltip"
  >
    <template #content>
      <div>{{ reason }}</div>
    </template>
    <template #reference>
      <slot />
    </template>
  </VTooltip>
  <VTooltip
    v-else
    :placement="placement"
    class="cursor-not-allowed"
    :class="attrs.class"
    :theme="THEME"
    :triggers="['hover', 'touch']"
    :show-group="showGroup"
    :popper-class="popperClass"
    data-testid="permission-tooltip"
  >
    <template #content>
      <div class="flex items-center gap-2">
        <VIcon name="triangle-alert" aria-hidden="true" />
        <div>{{ permissionMessage }}</div>
      </div>
    </template>
    <template #reference>
      <slot />
    </template>
  </VTooltip>
</template>
